import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, Clock, CheckCircle, X,
  SkipForward, Bell, BellOff, LogOut, ArrowUp, Mic2,
  LayoutDashboard, Wifi, WifiOff, MoreHorizontal, Settings,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import HostSettings from "@/components/HostSettings";
import CustomSongApproval from "@/components/CustomSongApproval";
import DemoBanner from "@/components/DemoBanner";
import { supabase } from "@/integrations/supabase/client";
import { subscribeQueueUpdate, sendQueueUpdate } from "@/lib/queueSignal";
import { verifyHostSession, clearHostSession, getStoredAccountType } from "@/lib/hostAuth";
import Logo from "@/components/Logo";

interface QueueRequest {
  id: string;
  song_id: string | null;
  singer_name: string;
  status: "queued" | "sung" | "cancelled" | "completed" | "removed";
  queue_position: number;
  created_at: string;
  custom_song_title?: string;
  custom_song_artist?: string;
  approval_status?: string;
  songs: { title: string; artist: string } | null;
}

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-white/10 rounded ${className}`} />
);

const HostShowMode = ({ demo = false }: { demo?: boolean } = { demo: false }) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // ── Server-verified auth guard ──────────────────────────────────────────────
  useEffect(() => {
    if (demo) return;
    verifyHostSession(false).then((result) => {
      if (!result.valid) { clearHostSession(); navigate("/host"); }
    });
  }, [navigate, demo]);

  // ── Impersonation ───────────────────────────────────────────────────────────
  const [impersonating] = useState(() => localStorage.getItem("impersonating") !== null);

  // ── Core state ──────────────────────────────────────────────────────────────
  const [queue, setQueue] = useState<QueueRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [host, setHost] = useState<any>(null);
  const [venue, setVenue] = useState<any>(null);
  const [connectionStatus, setConnectionStatus] = useState<"connecting" | "connected" | "disconnected">("connecting");
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const notificationsRef = useRef(false); // ref so subscription closure doesn't stale-close over state
  const venueRef = useRef<any>(null);    // ref so subscription closure always reads current venue
  const [songsSung, setSongsSung] = useState(0);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [processingMoveId, setProcessingMoveId] = useState<string | null>(null);
  const [showMenuOpen, setShowMenuOpen] = useState(false);
  const [showDemoBanner, setShowDemoBanner] = useState(true);

  // Keep venueRef current so stale subscription closures always read the live venue
  useEffect(() => { venueRef.current = venue; }, [venue]);

  // ── Demo banner timer ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!demo) return;
    const timer = setTimeout(() => setShowDemoBanner(false), 12000);
    return () => clearTimeout(timer);
  }, [demo]);

  // ── Demo mode data ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!demo) return;
    setVenue({ id: "demo-venue-id", name: "Demo Venue", slug: "demo", primary_color: "#7c3aed", secondary_color: "#06b6d4", allow_requests: true });
    setQueue([
      { id: "q1", song_id: "s1", singer_name: "Demo Singer", status: "queued", queue_position: 1, created_at: new Date().toISOString(), songs: { title: "Demo Song 1", artist: "Artist A" } },
      { id: "q2", song_id: "s2", singer_name: "Alex", status: "queued", queue_position: 2, created_at: new Date().toISOString(), songs: { title: "Demo Song 2", artist: "Artist B" } },
    ]);
    setConnectionStatus("connected");
    setLoading(false);
  }, [demo]);

  // ── Realtime subscription (non-demo) ────────────────────────────────────────
  useEffect(() => {
    if (demo) return;
    loadVenue();

    const channel = supabase
      .channel("show-mode-queue")
      .on("postgres_changes", { event: "*", schema: "public", table: "requests" }, (payload) => {
        setTimeout(() => loadQueue(), 100);
        if (payload.eventType === "INSERT" && notificationsRef.current) {
          playNotificationSound();
          toast({ title: "New Request", description: `${payload.new?.singer_name || "Someone"} wants to sing!`, duration: 5000 });
        }
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") setConnectionStatus("connected");
        else if (status === "CHANNEL_ERROR") {
          setConnectionStatus("disconnected");
          setTimeout(() => { setConnectionStatus("connecting"); loadQueue(); }, 5000);
        } else if (status === "CLOSED") setConnectionStatus("disconnected");
      });

    const unsubscribe = subscribeQueueUpdate(() => loadQueue());
    return () => { supabase.removeChannel(channel); unsubscribe(); };
  }, [demo]); // notificationsEnabled intentionally omitted — read via notificationsRef

  useEffect(() => {
    if (venue?.id) loadQueue();
  }, [venue?.id]);

  // ── Data loaders ────────────────────────────────────────────────────────────
  const loadVenue = async () => {
    try {
      const hostId = localStorage.getItem("hostId");
      const token = localStorage.getItem("hostSessionToken");
      if (!token) return;

      // get_host_self returns the caller's own record (no password_hash) and
      // applies the expired→basic downgrade server-side.
      const { data } = await (supabase.rpc as any)("get_host_self", { p_token: token });
      const hostData = (Array.isArray(data) ? data[0] : data) as any;

      if (hostData) setHost(hostData);

      if (hostData?.subscription_end_date && new Date(hostData.subscription_end_date) <= new Date()) {
        toast({ title: "Subscription Expired", description: "Your plan has been moved to Basic tier.", variant: "destructive" });
        // host accounts have no dashboard — send them back to login
        navigate(hostData?.account_type === "host" ? "/show-host" : "/host-dashboard");
        return;
      }

      // host accounts carry venue_id directly; venue_admins use the venue_hosts junction
      let venueRecord: any = null;
      if (hostData?.account_type === "host" && hostData?.venue_id) {
        const { data: vd } = await (supabase.from("venues") as any)
          .select("id, name, slug, welcome_message, primary_color, secondary_color, allow_requests, request_limit, auto_remove")
          .eq("id", hostData.venue_id).single();
        venueRecord = vd;
      } else {
        const { data: vhd } = await (supabase.from("venue_hosts") as any)
          .select("venues (id, name, slug, welcome_message, primary_color, secondary_color, allow_requests, request_limit, auto_remove)")
          .eq("host_id", hostId).single();
        venueRecord = vhd?.venues;
      }

      if (venueRecord) {
        setVenue(venueRecord);
        localStorage.setItem("currentVenueId", venueRecord.id);
      }
    } catch (e) {
      console.error("Error loading venue:", e);
    }
  };

  const loadQueue = async () => {
    const v = venueRef.current;
    if (!v?.id) { setLoading(false); return; }
    try {
      const { data, error } = await (supabase.from("requests") as any)
        .select("id, song_id, singer_name, status, queue_position, created_at, custom_song_title, custom_song_artist, approval_status, songs (title, artist)")
        .eq("venue_id", v.id)
        .eq("status", "queued")
        .eq("approval_status", "approved")
        .order("queue_position");
      if (!error) setQueue((data || []) as QueueRequest[]);
    } catch (e) {
      console.error("Error loading queue:", e);
    } finally {
      setLoading(false);
    }
  };

  const playNotificationSound = () => {
    const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmERBUOh4O6UUggOY7pzwG4sAA==");
    audio.volume = 0.3;
    audio.play().catch(() => {});
  };

  // ── Queue actions ───────────────────────────────────────────────────────────
  const markAsSung = async (requestId: string) => {
    const token = localStorage.getItem("hostSessionToken");
    if (!token) return;
    setCompletingId(requestId);
    try {
      // Run animation delay and RPC in parallel — total cost is max(280ms, rpc) not sum
      const [, rpcResult] = await Promise.all([
        new Promise((r) => setTimeout(r, 280)),
        (supabase.rpc as any)("update_request_status", {
          p_session_token: token, p_request_id: requestId, p_new_status: "completed",
        }),
      ]);
      const { error } = rpcResult as any;
      if (error) throw error;
      setQueue((prev) => prev.filter((r) => r.id !== requestId));
      setSongsSung((n) => n + 1);
      sendQueueUpdate({ action: "completed", requestId });
      toast({ title: "Marked as Sung" });
    } catch (e) {
      console.error("Error marking sung:", e);
      loadQueue();
      toast({ title: "Error", description: "Failed to update request", variant: "destructive" });
    } finally {
      setCompletingId(null);
    }
  };

  const skipSong = async (requestId: string) => {
    const token = localStorage.getItem("hostSessionToken");
    if (!token) return;
    try {
      const { error } = await (supabase.rpc as any)("update_request_status", {
        p_session_token: token, p_request_id: requestId, p_new_status: "removed",
      });
      if (error) throw error;
      setQueue((prev) => prev.filter((r) => r.id !== requestId));
      sendQueueUpdate({ action: "removed", requestId });
      toast({ title: "Song Skipped" });
    } catch (e) {
      console.error("Error skipping:", e);
      loadQueue();
      toast({ title: "Error", description: "Failed to skip", variant: "destructive" });
    }
  };

  const removeFromQueue = (requestId: string) => skipSong(requestId);

  const moveToNext = async (requestId: string) => {
    const token = localStorage.getItem("hostSessionToken");
    if (!token || !venue?.id) return;
    setProcessingMoveId(requestId);
    try {
      const { error } = await (supabase.rpc as any)("move_request_to_next", {
        p_session_token: token, p_request_id: requestId,
      });
      if (error) throw error;
      sendQueueUpdate({ action: "moved", requestId });
      toast({ title: "Moved to Next" });
      await loadQueue();
    } catch (e) {
      console.error("Error moving:", e);
      toast({ title: "Error", description: "Failed to move", variant: "destructive" });
      loadQueue();
    } finally {
      setProcessingMoveId(null);
    }
  };

  const handleToggleRequests = async () => {
    const token = localStorage.getItem("hostSessionToken");
    if (!token || !venue?.id) return;
    try {
      const newStatus = !venue.allow_requests;
      const { error } = await (supabase.rpc as any)("update_venue_queue_rules", {
        p_session_token: token,
        p_venue_id: venue.id,
        p_request_limit: venue.request_limit ?? 100,
        p_allow_requests: newStatus,
        p_auto_remove: venue.auto_remove ?? false,
      });
      if (error) throw error;
      setVenue({ ...venue, allow_requests: newStatus });
      toast({ title: newStatus ? "Requests Enabled" : "Requests Disabled" });
    } catch (e) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  const handleClearQueue = async () => {
    const token = localStorage.getItem("hostSessionToken");
    if (!token || !venue?.id || !confirm("Clear the entire queue? This cannot be undone.")) return;
    try {
      if (queue.length === 0) { toast({ title: "Queue Empty" }); return; }
      const { error } = await (supabase.rpc as any)("clear_venue_queue", {
        p_session_token: token, p_venue_id: venue.id,
      });
      if (error) throw error;
      setQueue([]);
      sendQueueUpdate({ action: "cleared" });
      toast({ title: "Queue Cleared" });
    } catch (e) {
      console.error("Error clearing:", e);
      toast({ title: "Error", description: "Failed to clear", variant: "destructive" });
    }
  };

  const handleExportQueue = async () => {
    try {
      if (queue.length === 0) { toast({ title: "Queue Empty", description: "Nothing to export" }); return; }
      const rows = queue.map((r, i) => ({
        Position: String(r.queue_position || i + 1),
        Singer: r.singer_name,
        Song: r.custom_song_title || r.songs?.title || "Unknown",
        Artist: r.custom_song_artist || r.songs?.artist || "Unknown",
        "Requested At": new Date(r.created_at).toLocaleString(),
      }));
      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(","),
        ...rows.map((row) => headers.map((h) => {
          const v = row[h as keyof typeof row];
          return v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
        }).join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${venue?.name || "Venue"}_Queue_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({ title: "Queue Exported" });
    } catch (e) {
      console.error("Error exporting:", e);
      toast({ title: "Error", description: "Failed to export", variant: "destructive" });
    }
  };

  const toggleNotifications = () => {
    const next = !notificationsEnabled;
    if (next && "Notification" in window) {
      Notification.requestPermission().then((perm) => {
        if (perm === "granted") {
          notificationsRef.current = true;
          setNotificationsEnabled(true);
          toast({ title: "Notifications Enabled" });
        }
      });
    } else {
      notificationsRef.current = next;
      setNotificationsEnabled(next);
      toast({ title: next ? "Audio Alerts Enabled" : "Notifications Disabled" });
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────────
  const nowPlaying = queue[0] ?? null;
  const upNext = queue.slice(1);
  const estimatedWait = queue.length * 4;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist relative">
      {/* Faint 48px grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
        }}
      />
      <div className="relative z-10">

      {/* Zone A: Sticky header */}
      <div className="sticky top-0 z-40 backdrop-blur-sm bg-console-bg/85 border-b border-console-line">
        <div className="container mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative">
              <Logo tone="text" size="sm" />
              <span className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ${
                getStoredAccountType() === "host"
                  ? "bg-console-signal shadow-[0_0_6px_#F5B53D]"
                  : "bg-console-primary shadow-[0_0_6px_#4D7CFE]"
              }`} />
            </div>
            <div className="min-w-0">
              <p className="font-geist text-[14px] font-semibold text-console-text leading-tight truncate tracking-tight">{venue?.name || "Show Mode"}</p>
              <p className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted leading-tight mt-1">Live Show</p>
            </div>
            <div className={`hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-[3px] font-geist-mono text-[10px] tracking-[0.12em] uppercase border ${
              connectionStatus === "connected"
                ? "bg-console-ok/10 text-console-ok border-console-ok/30"
                : connectionStatus === "connecting"
                ? "bg-console-signal/10 text-console-signal border-console-signal/30"
                : "bg-console-red/10 text-console-red border-console-red/30"
            }`}>
              {connectionStatus === "connected" ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              <span>{connectionStatus === "connected" ? "Live" : connectionStatus === "connecting" ? "Connecting" : "Offline"}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-text hover:bg-white/5 rounded-[6px]" onClick={toggleNotifications} title={notificationsEnabled ? "Disable notifications" : "Enable notifications"}>
              {notificationsEnabled ? <Bell className="h-4 w-4 text-console-signal" /> : <BellOff className="h-4 w-4" />}
            </Button>
            {/* Settings: host accounts go to dedicated page; venue admins get the inline dialog */}
            {getStoredAccountType() === "host"
              ? <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-text hover:bg-white/5 rounded-[6px]" onClick={() => navigate("/host-settings")} title="Settings"><Settings className="h-4 w-4" /></Button>
              : <HostSettings host={host} />
            }
            {/* Dashboard link: only for venue admins */}
            {getStoredAccountType() !== "host" && (
              <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-text hover:bg-white/5 rounded-[6px]" onClick={() => navigate("/host-dashboard")} title="Back to Dashboard">
                <LayoutDashboard className="h-4 w-4" />
              </Button>
            )}
            <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-red hover:bg-white/5 rounded-[6px]"
              onClick={() => { clearHostSession(); navigate(getStoredAccountType() === "host" ? "/show-host" : "/venue"); }}
              title="Sign out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Impersonation banner */}
      {impersonating && (
        <div className="bg-console-signal/15 border-b border-console-signal/30 px-4 py-2 text-center">
          <span className="font-geist text-console-signal text-[12px] font-medium">Viewing as host (impersonation)</span>
          <button
            className="ml-4 font-geist-mono text-[11px] text-console-signal underline"
            onClick={() => {
              const saved = localStorage.getItem("_savedSuperAdmin");
              if (saved) { const keys = JSON.parse(saved); Object.entries(keys).forEach(([k, v]) => localStorage.setItem(k, v as string)); localStorage.removeItem("_savedSuperAdmin"); }
              localStorage.removeItem("impersonating");
              navigate("/super-admin");
            }}
          >Exit</button>
        </div>
      )}

      {/* Demo banner */}
      {demo && showDemoBanner && (
        <DemoBanner onDismiss={() => setShowDemoBanner(false)} message="DEMO MODE — host actions are simulated." />
      )}

      <div className="container mx-auto px-4 sm:px-6 py-5 space-y-5">

        {/* Zone B: Now Playing */}
        {loading ? (
          <div className="rounded-[10px] border border-console-signal/30 bg-console-signal/5 p-5">
            <Skeleton className="h-4 w-28 mb-3" />
            <div className="flex items-start gap-4 mb-4">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1 space-y-2"><Skeleton className="h-5 w-48" /><Skeleton className="h-3.5 w-36" /><Skeleton className="h-3.5 w-24" /></div>
            </div>
            <div className="flex gap-3"><Skeleton className="h-12 flex-1" /><Skeleton className="h-12 flex-1" /></div>
          </div>
        ) : nowPlaying ? (
          <div className="rounded-[10px] border border-console-signal/40 bg-console-signal/5 p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="flex h-2 w-2 rounded-full bg-console-signal animate-pulse shadow-[0_0_8px_#F5B53D]" />
              <span className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-signal">On Air · Now Playing</span>
            </div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 bg-console-signal/20 rounded-full flex items-center justify-center shrink-0">
                <Mic2 className="h-5 w-5 text-console-signal" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-geist text-[20px] font-semibold text-console-text truncate tracking-[-0.02em]">
                  {nowPlaying.custom_song_title || nowPlaying.songs?.title || "Custom Song"}
                </p>
                <p className="font-geist text-[14px] text-console-muted truncate">
                  {nowPlaying.custom_song_artist || nowPlaying.songs?.artist}
                </p>
                <p className="font-geist text-[13px] font-medium text-console-signal mt-1">{nowPlaying.singer_name}</p>
              </div>
              {nowPlaying.custom_song_title && (
                <Badge className="bg-console-signal/20 text-console-signal border border-console-signal/30 font-geist-mono text-[10px] tracking-[0.16em] uppercase shrink-0 rounded-[3px]">CUSTOM</Badge>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                className={`flex-1 h-12 font-geist font-semibold text-[14px] bg-console-ok text-console-bg hover:bg-console-ok/90 rounded-[6px] transition-all duration-300 ${completingId === nowPlaying.id ? "translate-x-6 opacity-0" : ""}`}
                onClick={() => markAsSung(nowPlaying.id)}
                disabled={completingId === nowPlaying.id}
              >
                <CheckCircle className="mr-2 h-4 w-4" />Mark as Sung
              </Button>
              <Button
                variant="ghost"
                className="flex-1 h-12 font-geist font-medium text-[14px] border border-console-line-strong text-console-text hover:bg-white/5 hover:text-console-text rounded-[6px]"
                onClick={() => skipSong(nowPlaying.id)}
              >
                <SkipForward className="mr-2 h-4 w-4" />Skip
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-[10px] border border-console-line bg-console-surface p-8 text-center">
            <Mic2 className="h-12 w-12 text-console-dim mx-auto mb-3" />
            <p className="font-geist text-console-text font-medium">Queue is empty</p>
            <p className="font-geist text-[13px] text-console-muted mt-1">Share your venue page so guests can join</p>
          </div>
        )}

        {/* Zone C: Stats strip */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "In Queue", value: queue.length, icon: Users, colour: "text-console-primary", bg: "bg-console-primary/20" },
            { label: "Est. Wait", value: `${estimatedWait}m`, icon: Clock, colour: "text-console-muted", bg: "bg-white/5" },
            { label: "Tonight", value: songsSung, icon: CheckCircle, colour: "text-console-ok", bg: "bg-console-ok/20" },
          ].map(({ label, value, icon: Icon, colour, bg }) => (
            <div key={label} className="rounded-[10px] border border-console-line bg-console-surface p-2 sm:p-4 flex flex-col sm:flex-row items-center sm:gap-3 gap-1 text-center sm:text-left">
              <div className={`p-1.5 sm:p-2 rounded-full ${bg} shrink-0`}><Icon className={`h-4 w-4 ${colour}`} /></div>
              <div className="min-w-0">
                <p className="font-geist text-[16px] sm:text-[22px] font-semibold text-console-text leading-tight tracking-[-0.02em]">{value}</p>
                <p className="font-geist-mono text-[10px] tracking-[0.14em] uppercase text-console-muted leading-tight mt-1">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Zone D: Up Next list */}
        <div className="rounded-[10px] border border-console-line bg-console-surface overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-console-line">
            <div className="flex items-center gap-2.5">
              <span className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-signal">02 /</span>
              <Users className="h-4 w-4 text-console-primary" />
              <span className="font-geist text-[14px] font-semibold text-console-text tracking-tight">Up Next</span>
              <Badge className="bg-console-primary/20 text-console-primary border border-console-primary/30 font-geist-mono text-[10px] tracking-[0.12em] rounded-[3px]">{upNext.length}</Badge>
            </div>
            <div className="relative">
              <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-text hover:bg-white/5 rounded-[6px]" onClick={() => setShowMenuOpen((o) => !o)}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
              {showMenuOpen && (
                <div
                  className="absolute right-0 top-12 sm:top-10 z-20 w-48 rounded-[6px] border border-console-line bg-console-surface-2 shadow-xl py-1"
                  onMouseLeave={() => setShowMenuOpen(false)}
                >
                  <button className="w-full min-h-[44px] flex items-center px-3 font-geist text-[13px] hover:bg-white/5 text-console-text transition-colors" onClick={() => { handleToggleRequests(); setShowMenuOpen(false); }}>
                    {venue?.allow_requests ? "Disable Requests" : "Enable Requests"}
                  </button>
                  <button className="w-full min-h-[44px] flex items-center px-3 font-geist text-[13px] hover:bg-white/5 text-console-red transition-colors" onClick={() => { handleClearQueue(); setShowMenuOpen(false); }}>
                    Clear All Queue
                  </button>
                  <button className="w-full min-h-[44px] flex items-center px-3 font-geist text-[13px] hover:bg-white/5 text-console-text transition-colors" onClick={() => { handleExportQueue(); setShowMenuOpen(false); }}>
                    Export Queue (CSV)
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="divide-y divide-console-line">
            {loading ? (
              [1, 2, 3].map((n) => (
                <div key={n} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="w-7 h-7 rounded-full" />
                  <div className="flex-1 space-y-1.5"><Skeleton className="h-3.5 w-40" /><Skeleton className="h-3 w-28" /></div>
                  <Skeleton className="h-8 w-20" />
                </div>
              ))
            ) : upNext.length === 0 ? (
              <div className="text-center py-8 font-geist text-console-dim text-[13px]">No more songs queued</div>
            ) : (
              upNext.map((request, index) => (
                <div
                  key={request.id}
                  className={`flex items-center gap-3 px-4 py-3 transition-all duration-300 ${completingId === request.id ? "translate-x-6 opacity-0" : ""}`}
                >
                  <div className="w-7 h-7 bg-white/5 border border-console-line rounded-full flex items-center justify-center font-geist-mono text-[11px] font-semibold text-console-muted shrink-0">
                    {index + 2}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-geist text-[14px] font-medium text-console-text truncate">
                      {request.custom_song_title || request.songs?.title}
                      {request.custom_song_title && (
                        <span className="ml-1.5 font-geist-mono text-[10px] tracking-[0.12em] text-console-signal font-semibold">CUSTOM</span>
                      )}
                    </p>
                    <p className="font-geist text-[12px] text-console-muted truncate">
                      {request.custom_song_artist || request.songs?.artist} · {request.singer_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 px-0 text-console-primary hover:bg-console-primary/10 hover:text-console-primary rounded-[6px]"
                      onClick={() => moveToNext(request.id)}
                      disabled={processingMoveId === request.id}
                      title="Move to next"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 px-0 text-console-red hover:bg-red-500/10 hover:text-console-red rounded-[6px]"
                      onClick={() => removeFromQueue(request.id)}
                      title="Remove"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Zone E: Custom Song Approvals */}
        {venue?.id && <CustomSongApproval venueId={venue.id} showExportControls={false} />}
      </div>
      </div>
    </div>
  );
};

export default HostShowMode;
